import { Controller } from "@hotwired/stimulus"

const pendingStates = new Map()

// Owns the lifecycle of one dashboard Turbo frame. Turbo already preserves a
// populated frame until its replacement arrives; this controller adds busy
// semantics, bounded pending retries, local failures, and focus-safe recovery.
export default class extends Controller {
  static targets = ["status"]
  static values = {
    url: String,
    hasContent: { type: Boolean, default: false },
  }

  connect() {
    this.beforeFetch = this.beforeFetch.bind(this)
    this.frameLoaded = this.frameLoaded.bind(this)
    this.fetchFailed = this.fetchFailed.bind(this)
    this.frameMissing = this.frameMissing.bind(this)
    this.blockDuplicateFilter = this.blockDuplicateFilter.bind(this)
    this.element.addEventListener("turbo:before-fetch-request", this.beforeFetch)
    this.element.addEventListener("turbo:frame-load", this.frameLoaded)
    this.element.addEventListener("turbo:fetch-request-error", this.fetchFailed)
    this.element.addEventListener("click", this.blockDuplicateFilter, true)
    this.element.addEventListener("turbo:frame-missing", this.frameMissing)
    this.busy = this.element.getAttribute("aria-busy") === "true"

    if (this.awaitingBackground()) this.scheduleBackgroundChecks()
    else pendingStates.delete(this.stateKey())
  }

  disconnect() {
    this.element.removeEventListener("turbo:before-fetch-request", this.beforeFetch)
    this.element.removeEventListener("turbo:frame-load", this.frameLoaded)
    this.element.removeEventListener("turbo:fetch-request-error", this.fetchFailed)
    this.element.removeEventListener("click", this.blockDuplicateFilter, true)
    this.element.removeEventListener("turbo:frame-missing", this.frameMissing)
    this.clearTimers()
  }

  beforeFetch(event) {
    if (event.target !== this.element) return
    const active = document.activeElement
    if (active?.matches?.("[data-dashboard-filter]")) this.focusedFilterHref = active.getAttribute("href")
    this.setBusy(true)
    this.toggleFilters(false)
    if (this.hasContentValue) this.setStatus("Refreshing…")
  }

  frameLoaded(event) {
    if (event.target !== this.element) return
    const completedBackground = pendingStates.has(this.stateKey())
    this.setBusy(this.pending())
    this.toggleFilters(true)
    if (this.refreshing() && !this.pending()) {
      this.hasContentValue = true
      this.restoreFilterFocus()
    }

    if (this.awaitingBackground()) {
      this.scheduleBackgroundChecks()
      return
    }

    pendingStates.delete(this.stateKey())
    this.clearTimers()
    this.hasContentValue = true
    this.restoreFilterFocus()
    if (completedBackground) this.setStatus("Updated")
    if (this.retrying) {
      this.retrying = false
      this.dispatch("retry-success", { detail: { frame: this.element.id } })
      this.setStatus("Updated")
    }
  }

  fetchFailed(event) {
    if (event.target !== this.element) return
    event.preventDefault()
    this.showFailure("This section couldn't load.")
  }

  frameMissing(event) {
    if (event.target !== this.element) return
    event.preventDefault()
    this.showFailure("This section returned an incomplete response.")
  }

  retry(event) {
    event?.preventDefault()
    pendingStates.delete(this.stateKey())
    this.retrying = true
    this.setBusy(true)
    this.setStatus("Retrying…")
    this.reloadFrame()
  }

  blockDuplicateFilter(event) {
    const control = event.target.closest?.("[data-dashboard-filter]")
    if (!control) return
    if (this.busy) {
      event.preventDefault()
      event.stopImmediatePropagation()
    } else {
      this.focusedFilterHref = control.getAttribute("href")
    }
  }

  scheduleBackgroundChecks() {
    this.clearTimers()
    if (this.refreshing() && !this.staleReported) {
      this.staleReported = true
      queueMicrotask(() => this.dispatch("stale-refresh", { detail: { frame: this.element.id } }))
    }
    const key = this.stateKey()
    const state = pendingStates.get(key) || { startedAt: Date.now(), reloads: 0 }
    pendingStates.set(key, state)

    const elapsed = Date.now() - state.startedAt
    if (elapsed >= 30000) return this.pendingTimedOut()

    ;[5000, 15000].forEach((threshold, index) => {
      if (state.reloads > index || elapsed >= threshold) return
      this.timers ||= []
      this.timers.push(setTimeout(() => this.reloadBackground(state), threshold - elapsed))
    })
    this.timers ||= []
    this.timers.push(setTimeout(() => this.pendingTimedOut(), 30000 - elapsed))
  }

  reloadBackground(state) {
    if (!this.awaitingBackground()) return
    state.reloads += 1
    this.setStatus(this.pending() ? "Still building this section…" : "Still refreshing…")
    this.reloadFrame()
  }

  pendingTimedOut() {
    if (!this.awaitingBackground()) return
    const message = this.pending() ? "This section is taking longer than expected." : "Refresh delayed."
    this.showFailure(message, true)
    this.dispatch("pending-timeout", { detail: { frame: this.element.id } })
  }

  showFailure(message, keepPendingContent = false) {
    this.clearTimers()
    this.setBusy(false)
    this.toggleFilters(true)
    let retry

    if (this.hasContentValue || keepPendingContent) {
      this.setStatus("")
      const status = this.statusElement()
      retry = this.retryButton()
      status?.append(document.createTextNode(`${message} `), retry)
    } else {
      this.element.replaceChildren(this.failureCard(message))
      retry = this.element.querySelector('[data-action="dashboard-frame-state#retry"]')
    }
    retry?.focus({ preventScroll: true })
    this.dispatch("failed", { detail: { frame: this.element.id } })
  }

  failureCard(message) {
    const card = document.createElement("div")
    card.className = "min-h-40 mb-8 rounded-xl border border-rose-200 bg-rose-50/40 p-8 text-center"
    card.setAttribute("role", "alert")
    const heading = document.createElement("h2")
    heading.className = "font-semibold text-slate-900"
    heading.textContent = message
    const copy = document.createElement("p")
    copy.className = "mt-1 text-sm text-slate-600"
    copy.textContent = "Your existing tracking is safe. Try loading this section again."
    card.append(heading, copy, this.retryButton("Retry"))
    return card
  }

  retryButton(label = "Retry") {
    const button = document.createElement("button")
    button.type = "button"
    button.textContent = label
    button.className = "mt-3 font-medium text-violet-600"
    button.dataset.action = "dashboard-frame-state#retry"
    return button
  }

  pending() {
    return Boolean(this.element.querySelector("[data-dashboard-frame-pending]"))
  }

  refreshing() {
    return Boolean(this.element.querySelector("[data-dashboard-frame-refreshing]"))
  }

  awaitingBackground() {
    return this.pending() || this.refreshing()
  }

  reloadFrame() {
    if (this.element.getAttribute("src") !== this.urlValue) {
      this.element.setAttribute("src", this.urlValue)
    } else if (typeof this.element.reload === "function") {
      this.element.reload()
    }
  }

  setBusy(busy) {
    this.busy = busy
    this.element.setAttribute("aria-busy", busy ? "true" : "false")
  }

  restoreFilterFocus() {
    if (!this.focusedFilterHref) return
    const control = Array.from(this.element.querySelectorAll("[data-dashboard-filter]"))
      .find((item) => item.getAttribute("href") === this.focusedFilterHref)
    this.focusedFilterHref = null
    if (control) requestAnimationFrame(() => control.focus({ preventScroll: true }))
  }

  toggleFilters(enabled) {
    this.element.querySelectorAll("[data-dashboard-filter]").forEach((control) => {
      control.setAttribute("aria-disabled", enabled ? "false" : "true")
      control.classList.toggle("pointer-events-none", !enabled)
      control.classList.toggle("opacity-60", !enabled)
    })
  }

  setStatus(message) {
    const status = this.statusElement()
    if (status) status.textContent = message
  }

  statusElement() {
    return this.hasStatusTarget ? this.statusTarget : null
  }

  stateKey() {
    return `${this.element.id}:${this.urlValue}`
  }

  clearTimers() {
    ;(this.timers || []).forEach(clearTimeout)
    this.timers = []
  }
}
